// Página pública de sorteos:
//   /sorteo?r=TOKEN  → compra en línea: escoge cantidad, da sus datos, acepta
//                      las reglas y paga con tarjeta EN LA MISMA PANTALLA.
//                      Los números se asignan solo cuando Stripe aprueba el
//                      pago (pedido de Tuto 2026-10-31: antes se reservaban
//                      antes de pagar y se veían "pendientes" en el mapa).
//   /sorteo?t=TOKEN  → link de pago de una venta reservada por el staff, o
//                      la página de una venta ya pagada (boletos virtuales).
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Paper, Typography, Stack, Box, TextField, Button, Alert, CircularProgress, Divider, Chip, MenuItem, Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Logo from '../../components/logo';
import TicketGallery from '../../components/raffle-ticket/TicketGallery';
import { FN_URLS, money, fmtDateEs, callFn, METHOD_LABELS } from '../../utils/raffles';

const stripePromise = loadStripe('pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6');
const RED = '#d40100';
const CARD_STYLE = { hidePostalCode: false, style: { base: { fontSize: '16px', color: '#222', '::placeholder': { color: '#aab7c4' } } } };

// Reglas y condiciones (español / inglés) en un acordeón. Se abre solo si el
// enlace trae #reglas (desde el email) o cuando el comprador toca "ver reglas".
function RulesSection({ rulesEs, rulesEn, open, onToggle }) {
  const [lang, setLang] = useState(rulesEs ? 'es' : 'en');
  if (!rulesEs && !rulesEn) return null;
  const text = lang === 'es' ? rulesEs || rulesEn : rulesEn || rulesEs;
  const lines = String(text).split('\n').map((l) => l.replace(/^\s*[•·\-*]\s*/, '').trim()).filter(Boolean);
  return (
    <Accordion id="reglas" expanded={open} onChange={(_, v) => onToggle(v)} disableGutters elevation={0} sx={{ mt: 3, border: '1px solid #e0dcd2', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={600}>Reglas y condiciones del sorteo · Raffle rules</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {rulesEs && rulesEn && (
          <ToggleButtonGroup exclusive size="small" value={lang} onChange={(_, v) => v && setLang(v)} sx={{ mb: 1.5 }}>
            <ToggleButton value="es">Español</ToggleButton>
            <ToggleButton value="en">English</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75, fontSize: 14, lineHeight: 1.5 } }}>
          {lines.map((l, i) => (
            /^[A-ZÁÉÍÓÚÑ\s&,.]{12,}$/.test(l)
              ? <Typography key={i} component="li" sx={{ listStyle: 'none', ml: -2.5, mt: 1.5, fontWeight: 700, fontSize: 13 }}>{l}</Typography>
              : <li key={i}>{l}</li>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

function AcceptTerms({ checked, onChange, hasRules, onShowRules }) {
  return (
    <FormControlLabel
      sx={{ alignItems: 'flex-start', mr: 0 }}
      control={<Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ mt: -1, color: RED, '&.Mui-checked': { color: RED } }} />}
      label={(
        <Typography variant="body2">
          He leído y acepto las {hasRules ? <Link component="button" type="button" onClick={onShowRules} sx={{ color: RED, verticalAlign: 'baseline' }}>reglas y condiciones del sorteo</Link> : 'reglas y condiciones del sorteo'}. Confirmo que tengo 21 años o más.
          <Typography component="span" variant="caption" color="text.secondary" display="block">I have read and accept the raffle rules and conditions and confirm I am 21 or older.</Typography>
        </Typography>
      )}
    />
  );
}

// Pago de una venta reservada por el staff (link /sorteo?t=): la venta ya existe.
function CardForm({ token, amountCents, disabled, onPaid, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    try {
      const { clientSecret, paymentIntentId } = await callFn(FN_URLS.createIntent, { token });
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: elements.getElement(CardElement) } });
      if (error) throw new Error(error.message || 'No se pudo procesar la tarjeta.');
      if (paymentIntent.status !== 'succeeded') throw new Error('El pago no se completó. Intenta de nuevo.');
      const r = await callFn(FN_URLS.confirmPayment, { token, paymentIntentId: paymentIntentId || paymentIntent.id });
      onPaid(r.sale);
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box>
      <Box sx={{ p: 2, border: '1px solid #d7d2c6', borderRadius: 1.5, mb: 2, bgcolor: '#fff' }}>
        <CardElement options={CARD_STYLE} />
      </Box>
      <Button fullWidth size="large" variant="contained" disabled={!stripe || busy || disabled} onClick={pay} sx={{ height: 52, bgcolor: RED, '&:hover': { bgcolor: '#a30000' } }}>
        {busy ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : `Pagar ${money(amountCents)}`}
      </Button>
      <Typography variant="caption" color="text.secondary" display="block" mt={1} textAlign="center">Pago seguro con tarjeta procesado por Stripe.</Typography>
    </Box>
  );
}

function SaleView({ sale, token, onUpdate }) {
  const [error, setError] = useState('');
  const [justPaid, setJustPaid] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(window.location.hash === '#reglas');
  const paid = sale.status === 'paid';
  const expired = sale.status === 'expired' || sale.status === 'cancelled';
  const hasRules = Boolean(sale.rulesEs || sale.rulesEn);
  const showRules = () => { setRulesOpen(true); setTimeout(() => { const el = document.getElementById('reglas'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50); };
  return (
    <>
      <Typography variant="overline" color="text.secondary">{sale.raffleName}</Typography>
      <Typography variant="h4" sx={{ mb: 0.5 }}>{paid ? 'Tus boletos' : 'Paga tus boletos'}</Typography>
      {sale.drawDate && <Typography variant="body2" color="text.secondary">Sorteo: {fmtDateEs(sale.drawDate)}</Typography>}
      <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
        <Chip size="small" color={paid ? 'success' : expired ? 'error' : 'warning'} label={paid ? 'Pagado' : expired ? 'Reserva vencida o cancelada' : 'Pendiente de pago'} />
        {paid && sale.depositedAt && <Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label={`${sale.qty > 1 ? 'Boletos' : 'Boleto'} en la tómbola · ${new Date(sale.depositedAt).toLocaleDateString('es-PR')}`} />}
      </Stack>
      {justPaid && <Alert severity="success" sx={{ mt: 3 }}><strong>¡Pago recibido!</strong> Te enviamos tus números por email{sale.phone ? ' y SMS' : ''}. ¡Suerte!</Alert>}
      <Box sx={{ bgcolor: '#F4F6F8', borderRadius: 2, p: 2.5, mt: 3, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">Boleto{sale.qty > 1 ? 's' : ''}</Typography>
        <Typography variant="h3" sx={{ color: RED, lineHeight: 1.2 }}>{sale.numbersLabel}</Typography>
        <Typography variant="body2" mt={1}>{sale.buyerName}</Typography>
        <Typography variant="body2" color="text.secondary">{sale.qty} × {money(sale.amountCents / Math.max(1, sale.qty))} = <strong>{money(sale.amountCents)}</strong></Typography>
      </Box>
      {expired && <Alert severity="error" sx={{ mt: 3 }}>Esta reserva ya no está activa. Comunícate con {sale.contactName || 'B&B Target Center'}{sale.contactPhone ? ` al ${sale.contactPhone}` : ''} o vuelve a comprar.</Alert>}
      {!paid && !expired && (
        <Box mt={3}>
          {sale.holdExpiresAt && <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>Tus números están reservados hasta las {new Date(sale.holdExpiresAt).toLocaleTimeString('es-PR', { hour: 'numeric', minute: '2-digit' })}. Si no pagas antes, se liberan.</Typography>}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Box mb={2}><AcceptTerms checked={accepted} onChange={setAccepted} hasRules={hasRules} onShowRules={showRules} /></Box>
          <Elements stripe={stripePromise}>
            <CardForm token={token} amountCents={sale.amountCents} disabled={!accepted} onPaid={(s) => { onUpdate(s); setJustPaid(true); setError(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }} onError={setError} />
          </Elements>
        </Box>
      )}
      {paid && sale.raffle && (
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>Tus boletos virtuales</Typography>
          <Typography variant="body2" color="text.secondary" mb={1.5}>Descárgalos o imprímelos. Son tu comprobante junto con el email.</Typography>
          <TicketGallery raffle={sale.raffle} numbers={sale.numbers} buyer={{ name: sale.buyerName, phone: sale.phone, email: sale.email }} raffleName={sale.raffleName} />
        </Box>
      )}
      {paid && sale.payments && sale.payments.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" mt={2}>Pagado el {new Date(sale.payments[sale.payments.length - 1].paidAt).toLocaleDateString('es-PR')} · {METHOD_LABELS[sale.payments[sale.payments.length - 1].method] || 'tarjeta'}</Typography>
      )}
      {sale.terms && <Typography variant="caption" color="text.secondary" display="block" mt={2}>{sale.terms}</Typography>}
      <RulesSection rulesEs={sale.rulesEs} rulesEn={sale.rulesEn} open={rulesOpen} onToggle={setRulesOpen} />
      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary" textAlign="center">Dudas: {sale.contactName || 'B&B Target Center'}{sale.contactPhone ? ` · ${sale.contactPhone}` : ''}{sale.contactEmail ? ` · ${sale.contactEmail}` : ''}</Typography>
    </>
  );
}

// Después de que Stripe aprobó el cobro, pide los números al servidor. Si la
// red falla reintenta (hasta 3 veces); si se agotaron/reembolsaron, no.
async function confirmWithRetry(body, attempt = 1) {
  try {
    return await callFn(FN_URLS.onlineConfirm, body);
  } catch (e) {
    if (attempt >= 3 || /agotaron|reembols|no corresponde/i.test(e.message)) throw e;
    await new Promise((r) => setTimeout(r, 1500));
    return confirmWithRetry(body, attempt + 1);
  }
}

// Compra en línea: datos + tarjeta en la misma pantalla. Primero se crea el
// cobro (sin números), Stripe lo aprueba y solo entonces el servidor asigna
// los próximos números libres y crea la venta ya pagada.
function BuyForm({ raffle, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [qty, setQty] = useState(1);
  const [info, setInfo] = useState({ name: '', phone: '', email: '' });
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [rulesOpen, setRulesOpen] = useState(window.location.hash === '#reglas');
  const price = Math.round(raffle.ticketPrice * 100);
  const maxQty = Math.min(raffle.maxOnlineQty || 20, raffle.onlineAvailable || 0);
  const hasRules = Boolean(raffle.rulesEs || raffle.rulesEn);
  const ready = Boolean(stripe && elements && info.name.trim() && info.phone.trim() && info.email.trim() && accepted);
  const showRules = () => { setRulesOpen(true); setTimeout(() => { const el = document.getElementById('reglas'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50); };

  const pay = async () => {
    if (!ready) return;
    setBusy(true); setError('');
    try {
      const raffleToken = new URLSearchParams(window.location.search).get('r');
      const { clientSecret, paymentIntentId, checkoutToken } = await callFn(FN_URLS.onlineIntent, { raffleToken, qty, ...info, acceptTerms: accepted });
      const { error: cardError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, { payment_method: { card: elements.getElement(CardElement) } });
      if (cardError) throw new Error(cardError.message || 'No se pudo procesar la tarjeta.');
      if (paymentIntent.status !== 'succeeded') throw new Error('El pago no se completó. Intenta de nuevo.');
      // El cobro ya pasó: pedir los números (reintenta si la red falló).
      const result = await confirmWithRetry({ checkoutToken, paymentIntentId: paymentIntentId || paymentIntent.id });
      onPaid(result.sale, result.token);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6">Compra tus boletos</Typography>
      <TextField select fullWidth label="Cantidad de boletos" value={qty} onChange={(e) => setQty(Number(e.target.value))}>
        {Array.from({ length: maxQty }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>{n} boleto{n > 1 ? 's' : ''} · {money(n * price)}</MenuItem>)}
      </TextField>
      <TextField fullWidth label="Nombre completo *" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} autoComplete="name" />
      <TextField fullWidth label="Teléfono (celular) *" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} helperText="Te enviamos tus números por SMS." autoComplete="tel" />
      <TextField fullWidth label="Email *" type="email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} helperText="Aquí te llega el comprobante con tus números." autoComplete="email" />
      <Box>
        <Typography variant="subtitle2" gutterBottom>Tarjeta</Typography>
        <Box sx={{ p: 2, border: '1px solid #d7d2c6', borderRadius: 1.5, bgcolor: '#fff' }}>
          <CardElement options={CARD_STYLE} />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">Tus números se asignan automáticamente (los próximos disponibles) en cuanto se apruebe el pago. Los verás en pantalla y te llegan por email y SMS.</Typography>
      <AcceptTerms checked={accepted} onChange={setAccepted} hasRules={hasRules} onShowRules={showRules} />
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      <Button size="large" variant="contained" disabled={busy || !ready} onClick={pay} sx={{ height: 52, bgcolor: RED, '&:hover': { bgcolor: '#a30000' } }}>
        {busy ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : `Pagar ${money(qty * price)}`}
      </Button>
      <Typography variant="caption" color="text.secondary" textAlign="center">Pago seguro con tarjeta procesado por Stripe.</Typography>
      <RulesSection rulesEs={raffle.rulesEs} rulesEn={raffle.rulesEn} open={rulesOpen} onToggle={setRulesOpen} />
    </Stack>
  );
}

function BuyView({ raffle, onPaid }) {
  const [rulesOpen, setRulesOpen] = useState(window.location.hash === '#reglas');
  const price = Math.round(raffle.ticketPrice * 100);
  const canBuy = raffle.status === 'active' && raffle.onlineEnabled && raffle.onlineAvailable > 0;
  return (
    <>
      <Typography variant="overline" color="text.secondary">Sorteo</Typography>
      <Typography variant="h4" sx={{ mb: 0.5 }}>{raffle.name}</Typography>
      {raffle.prize && <Typography variant="subtitle1" sx={{ color: RED }}>{raffle.prize}</Typography>}
      <Typography variant="body2" color="text.secondary">{raffle.drawDate ? `Sorteo el ${fmtDateEs(raffle.drawDate)}` : ''}{raffle.venue ? ` · ${raffle.venue}` : ''}</Typography>
      {raffle.imageUrl && <Box component="img" src={raffle.imageUrl} alt="" sx={{ width: '100%', borderRadius: 2, mt: 2 }} />}
      {raffle.description && <Typography variant="body2" mt={2} sx={{ whiteSpace: 'pre-line' }}>{raffle.description}</Typography>}
      <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
        <Chip label={`${money(price)} por boleto`} />
        <Chip variant="outlined" label={`${raffle.sold} de ${raffle.totalTickets} vendidos`} />
        {canBuy && <Chip variant="outlined" color="success" label={`${raffle.onlineAvailable} disponibles en línea`} />}
      </Stack>
      <Divider sx={{ my: 3 }} />
      {!canBuy ? (
        <>
          <Alert severity="warning">{raffle.status !== 'active' ? 'Este sorteo ya cerró.' : !raffle.onlineEnabled ? 'La venta en línea no está disponible por ahora.' : 'Los boletos en línea se agotaron.'} Para boletos físicos, comunícate con {raffle.contactName || 'B&B Target Center'}{raffle.contactPhone ? ` al ${raffle.contactPhone}` : ''}.</Alert>
          <RulesSection rulesEs={raffle.rulesEs} rulesEn={raffle.rulesEn} open={rulesOpen} onToggle={setRulesOpen} />
        </>
      ) : (
        <Elements stripe={stripePromise}>
          <BuyForm raffle={raffle} onPaid={onPaid} />
        </Elements>
      )}
      {raffle.terms && <Typography variant="caption" color="text.secondary" display="block" mt={3}>{raffle.terms}</Typography>}
      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary" textAlign="center">Dudas: {raffle.contactName || 'B&B Target Center'}{raffle.contactPhone ? ` · ${raffle.contactPhone}` : ''}{raffle.contactEmail ? ` · ${raffle.contactEmail}` : ''}</Typography>
    </>
  );
}

export default function RafflePage() {
  const params = new URLSearchParams(window.location.search);
  const raffleToken = params.get('r') || '';
  const [saleToken, setSaleToken] = useState(params.get('t') || '');
  const [raffle, setRaffle] = useState(null);
  const [sale, setSale] = useState(null);
  const [justPaid, setJustPaid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sale && sale.token === saleToken) return undefined; // venta recién creada en línea: ya la tenemos
    setLoading(true);
    const body = saleToken ? { saleToken } : { raffleToken };
    if (!saleToken && !raffleToken) { setError('Este enlace no es válido.'); setLoading(false); return undefined; }
    callFn(FN_URLS.publicGet, body)
      .then((r) => { if (r.sale) setSale(r.sale); if (r.raffle) setRaffle(r.raffle); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleToken, raffleToken]);

  const onPaidOnline = (s, t) => {
    setSale({ ...s, token: t });
    setJustPaid(true);
    setSaleToken(t);
    window.history.replaceState(null, '', `/sorteo?t=${t}`);
    window.scrollTo({ top: 0 });
  };

  const title = sale ? sale.raffleName : raffle ? raffle.name : 'Sorteo';
  return (
    <>
      <Helmet><title>{title} · B&B Target Center</title></Helmet>
      <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6F8', py: { xs: 3, md: 6 } }}>
        <Container maxWidth={sale && sale.status === 'paid' ? 'md' : 'sm'}>
          <Stack alignItems="center" mb={3}><Logo disabledLink /></Stack>
          <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
            {loading ? <Box textAlign="center" py={6}><CircularProgress /></Box>
              : error && !sale && !raffle ? <Alert severity="error">{error}</Alert>
                : sale ? (
                  <>
                    {justPaid && <Alert severity="success" sx={{ mb: 2 }}><strong>¡Pago recibido!</strong> Estos son tus números. Te los enviamos también por email{sale.phone ? ' y SMS' : ''}. ¡Suerte!</Alert>}
                    <SaleView sale={sale} token={saleToken} onUpdate={setSale} />
                  </>
                )
                  : <BuyView raffle={raffle} onPaid={onPaidOnline} />}
          </Paper>
        </Container>
      </Box>
    </>
  );
}
